import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { theme } from '../theme';
import { PlanCard, type PlanModel } from '../components/PlanCard';
import { Navbar } from '../components/Navbar';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

function parseFeatures(f: unknown): string[] {
  if (Array.isArray(f)) return f.map(String);
  return [];
}

export function PricingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<PlanModel[]>([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bizId, setBizId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{
          plans: {
            id: string;
            name: string;
            tagline?: string | null;
            price_monthly: string;
            price_yearly: string;
            agents_count: number;
            features: unknown;
          }[];
        }>('/plans');
        setPlans(
          data.plans.map((p) => ({
            id: p.id,
            name: p.name,
            tagline: p.tagline ?? undefined,
            price_monthly: p.price_monthly,
            price_yearly: p.price_yearly,
            agents_count: p.agents_count,
            features: parseFeatures(p.features),
          }))
        );
        const token = localStorage.getItem('odc_access_token');
        if (token) {
          try {
            const b = await api.get<{ business: { id: string } | null }>('/business/me');
            setBizId(b.data.business?.id ?? null);
          } catch {
            setBizId(null);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function subscribe(planId: string) {
    if (!user || !bizId) {
      window.location.href = '/login';
      return;
    }
    await api.post('/plans/subscribe', {
      businessId: bizId,
      planId,
      billingCycle: yearly ? 'yearly' : 'monthly',
    });
    alert('Subscribed! (demo)');
  }

  return (
    <div className="odc-page relative z-10 min-h-screen" style={{ background: 'var(--bg)' }}>
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-16">
        <h1 className="text-center text-4xl font-black" style={{ color: 'var(--text)' }}>
          Plans built for scale
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-center" style={{ color: 'var(--muted)' }}>
          From local shops to full agencies — monthly or yearly (yearly ≈ 2 months free on listed totals).
        </p>

        <div className="mt-10 flex justify-center gap-3">
          <button
            type="button"
            className="rounded-full px-5 py-2 text-sm font-bold"
            style={{
              background: !yearly ? theme.primary : 'transparent',
              color: !yearly ? '#fff' : theme.textSecondary,
              border: `1px solid ${theme.border}`,
            }}
            onClick={() => setYearly(false)}
          >
            Monthly
          </button>
          <button
            type="button"
            className="rounded-full px-5 py-2 text-sm font-bold"
            style={{
              background: yearly ? theme.primary : 'transparent',
              color: yearly ? '#fff' : theme.textSecondary,
              border: `1px solid ${theme.border}`,
            }}
            onClick={() => setYearly(true)}
          >
            Yearly
          </button>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                yearly={yearly}
                highlight={p.name === 'Pro'}
                agency={p.name === 'Agency'}
                ctaPrimary={p.name === 'Pro'}
                onSelect={() => void subscribe(p.id)}
                ctaLabel={user ? 'Subscribe' : 'Start free'}
              />
            ))}
          </div>
        )}

        <div
          className="mt-20 overflow-x-auto rounded-2xl border"
          style={{ borderColor: theme.border, background: theme.card }}
        >
          <table className="odc-table min-w-[720px] text-left text-sm">
            <thead>
              <tr>
                <th className="p-4">Feature</th>
                {plans.map((p) => (
                  <th key={p.id} className="p-4">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ color: theme.textSecondary }}>
              {(
                [
                  { label: 'AI agents', test: (p: PlanModel) => p.agents_count > 0 },
                  {
                    label: 'Social & content',
                    test: (p: PlanModel) =>
                      p.features.some((f) => /social|content|calendar|whatsapp|schedul/i.test(f)),
                  },
                  {
                    label: 'AI calling',
                    test: (p: PlanModel) => p.features.some((f) => /call|voice/i.test(f)),
                  },
                  {
                    label: 'Analytics',
                    test: (p: PlanModel) => p.features.some((f) => /analytics|reporting|dashboard/i.test(f)),
                  },
                  {
                    label: 'Priority support',
                    test: (p: PlanModel) => p.features.some((f) => /support|sla|success/i.test(f)),
                  },
                  { label: 'Agency mode', test: (p: PlanModel) => p.name === 'Agency' },
                ] as const
              ).map(({ label, test }) => (
                <tr key={label} className="border-t" style={{ borderColor: theme.border }}>
                  <td className="p-4" style={{ color: theme.text }}>
                    {label}
                  </td>
                  {plans.map((p) => (
                    <td key={p.id} className="p-4">
                      {test(p) ? '✓' : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
